import { useState, useMemo } from "react";
import { cn } from "../../lib/utils";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowLeft02Icon, CheckmarkCircle02Icon, Cancel01Icon } from "@hugeicons/core-free-icons";
import {
  SUBREDDIT_STRATEGIES,
  SUBREDDIT_CATEGORIES,
  type SubredditEntry,
} from "../../data/reddit-channels";
import type { Card } from "../../data/types";
import { getTaskMeta, sortCardsByExecution } from "../../lib/taskMeta";

interface RedditChannelPageProps {
  cards: Card[];
  onBack: () => void;
  onSelectCard: (cardId: string) => void;
}

export function RedditChannelPage({ cards, onBack, onSelectCard }: RedditChannelPageProps) {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<SubredditEntry["category"] | "all">("all");
  const [promoFilter, setPromoFilter] = useState<"all" | "yes" | "no">("all");

  const filtered = useMemo(() => {
    return SUBREDDIT_STRATEGIES.filter((s) => {
      if (categoryFilter !== "all" && s.category !== categoryFilter) return false;
      if (promoFilter === "yes" && !s.allowsPromo) return false;
      if (promoFilter === "no" && s.allowsPromo) return false;
      if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [search, categoryFilter, promoFilter]);

  const grouped = useMemo(() => {
    const map = new Map<SubredditEntry["category"], SubredditEntry[]>();
    for (const s of filtered) {
      const existing = map.get(s.category) ?? [];
      existing.push(s);
      map.set(s.category, existing);
    }
    return map;
  }, [filtered]);

  const categoryOrder: SubredditEntry["category"][] = ["startup", "marketing", "tech", "general", "seo"];

  const redditExecutionSlots = useMemo(() => {
    const slots = cards.filter((card) => {
      if (card.laneId !== "channels") return false;
      const channel = (card.channel ?? "").toLowerCase();
      const title = card.title.toLowerCase();
      const meta = getTaskMeta(card);
      if (meta.taskType === "unknown") return false;
      return channel.includes("reddit") || title.includes("reddit");
    });

    return sortCardsByExecution(slots);
  }, [cards]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="shrink-0 px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)] flex items-center gap-[var(--spacing-3)]">
        <button
          onClick={onBack}
          className={cn(
            "h-[32px] w-[32px] rounded-[var(--radius-md)] flex items-center justify-center",
            "text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]",
            "transition-colors duration-[var(--duration-fast)] cursor-pointer",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
          )}
          aria-label="Back to channels"
        >
          <HugeiconsIcon icon={ArrowLeft02Icon} size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-[14px] font-semibold text-[var(--color-text-primary)]">Reddit Channels</h2>
          <span className="text-[14px] text-[var(--color-text-muted)]">{filtered.length} subreddits · {SUBREDDIT_STRATEGIES.filter((s) => s.allowsPromo).length} allow promotion</span>
        </div>
      </div>

      {/* Filters */}
      <div className="shrink-0 px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)] flex items-center gap-[var(--spacing-2)] flex-wrap">
        <input
          type="text"
          placeholder="Search subreddits…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={cn(
            "h-[32px] w-[220px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)]",
            "text-[14px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]",
            "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
            "hover:border-[var(--color-bg-hover)] transition-colors",
          )}
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as SubredditEntry["category"] | "all")}
          className={cn(
            "h-[32px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)] pr-[var(--spacing-6)]",
            "text-[14px] text-[var(--color-text-primary)] appearance-none cursor-pointer",
            "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
          )}
        >
          <option value="all">All Categories</option>
          {categoryOrder.map((cat) => (
            <option key={cat} value={cat}>{SUBREDDIT_CATEGORIES[cat]}</option>
          ))}
        </select>
        <select
          value={promoFilter}
          onChange={(e) => setPromoFilter(e.target.value as "all" | "yes" | "no")}
          className={cn(
            "h-[32px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)] pr-[var(--spacing-6)]",
            "text-[14px] text-[var(--color-text-primary)] appearance-none cursor-pointer",
            "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
          )}
        >
          <option value="all">All Promotion Rules</option>
          <option value="yes">Allows Promotion</option>
          <option value="no">No Direct Promo</option>
        </select>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-[var(--spacing-4)] py-[var(--spacing-4)] space-y-[var(--spacing-6)]">
          {redditExecutionSlots.length > 0 && (
            <div className="rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)] overflow-hidden">
              <div className="px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)]">
                <span className="text-[14px] font-semibold text-[var(--color-text-primary)]">Execution Slots</span>
              </div>
              <div className="divide-y divide-[var(--color-border-stroke)]">
                {redditExecutionSlots.map((slot) => {
                  const meta = getTaskMeta(slot);
                  return (
                    <button
                      key={slot.id}
                      onClick={() => onSelectCard(slot.id)}
                      className={cn(
                        "w-full text-left px-[var(--spacing-4)] py-[var(--spacing-2)]",
                        "hover:bg-[var(--color-bg-hover)] transition-colors duration-[var(--duration-fast)] cursor-pointer",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-focus)]",
                      )}
                    >
                      <div className="flex items-center gap-[var(--spacing-2)]">
                        <span className="text-[14px] text-[var(--color-text-primary)] flex-1 truncate">{slot.title}</span>
                        {meta.executionWindow && (
                          <span className="text-[11px] text-[var(--color-text-muted)] border border-[var(--color-border-stroke)] rounded-[var(--radius-sm)] px-[6px] py-[2px]">
                            {meta.executionWindow.replace("_CET", " CET")}
                          </span>
                        )}
                        {meta.sla && (
                          <span className="text-[11px] text-[var(--color-text-muted)] border border-[var(--color-border-stroke)] rounded-[var(--radius-sm)] px-[6px] py-[2px]">
                            SLA {meta.sla}
                          </span>
                        )}
                        <span className="text-[11px] text-[var(--color-text-muted)] capitalize">{slot.status.replace("_", " ")}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {categoryOrder.map((cat) => {
            const items = grouped.get(cat);
            if (!items || items.length === 0) return null;
            return (
              <div key={cat}>
                <div className="flex items-center gap-[var(--spacing-2)] mb-[var(--spacing-3)]">
                  <h3 className="text-[14px] font-semibold text-[var(--color-text-muted)] uppercase tracking-[0.04em]">
                    {SUBREDDIT_CATEGORIES[cat]}
                  </h3>
                  <span className="text-[14px] tabular-nums text-[var(--color-text-muted)]">{items.length}</span>
                </div>
                <div className="space-y-[var(--spacing-2)]">
                  {items.map((sub, idx) => (
                    <SubredditCard key={sub.name} entry={sub} index={idx} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SubredditCard({ entry, index }: { entry: SubredditEntry; index: number }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={cn(
        "rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)]",
        "transition-colors duration-[var(--duration-fast)]",
        "animate-list-item-in",
      )}
      style={{ animationDelay: `${Math.min(index, 9) * 30}ms` }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          "w-full flex items-center gap-[var(--spacing-3)] px-[var(--spacing-4)] py-[var(--spacing-3)]",
          "hover:bg-[var(--color-bg-hover)] transition-colors duration-[var(--duration-fast)] cursor-pointer text-left",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-focus)]",
          "rounded-[var(--radius-lg)]",
        )}
      >
        <span className="text-[14px] font-semibold text-[var(--color-text-primary)] flex-1">{entry.name}</span>
        <span className={cn(
          "inline-flex items-center gap-[var(--spacing-1)] rounded-[var(--radius-sm)] px-[6px] py-[1px] text-[14px] font-medium ring-1 ring-inset",
          entry.allowsPromo
            ? "bg-[color-mix(in_srgb,var(--color-state-success)_12%,transparent)] text-[var(--color-state-success)] ring-[color-mix(in_srgb,var(--color-state-success)_22%,transparent)]"
            : "bg-[var(--color-bg-hover)] text-[var(--color-text-muted)] ring-[var(--color-border-stroke)]",
        )}>
          {entry.allowsPromo ? (
            <HugeiconsIcon icon={CheckmarkCircle02Icon} size={12} />
          ) : (
            <HugeiconsIcon icon={Cancel01Icon} size={12} />
          )}
          {entry.allowsPromo ? "Promo OK" : "No Promo"}
        </span>
        <span className="text-[14px] text-[var(--color-text-muted)]">{expanded ? "▾" : "▸"}</span>
      </button>

      {expanded && (
        <div className="px-[var(--spacing-4)] pb-[var(--spacing-4)] animate-accordion-open">
          {entry.notes && (
            <p className="text-[14px] text-[var(--color-text-secondary)] mb-[var(--spacing-3)]">{entry.notes}</p>
          )}
          {entry.strategy && (
            <div className="mb-[var(--spacing-3)]">
              <span className="block text-[14px] font-semibold text-[var(--color-text-muted)] mb-[var(--spacing-1)]">Strategy</span>
              <p className="text-[14px] text-[var(--color-text-secondary)]">{entry.strategy}</p>
            </div>
          )}
          {entry.format && entry.format.length > 0 && (
            <div>
              <span className="block text-[14px] font-semibold text-[var(--color-text-muted)] mb-[var(--spacing-1)]">Format</span>
              <ul className="space-y-[var(--spacing-0_5)]">
                {entry.format.map((f, i) => (
                  <li key={i} className="flex items-start gap-[var(--spacing-2)] text-[14px] text-[var(--color-text-secondary)]">
                    <span className="text-[var(--color-text-muted)] shrink-0 mt-[2px]">·</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
